// The damage figure (R538): the number that pops at a struck hull.
//
// A figure appears when the commander's OWN fleet takes damage — the taken
// red, with a minus sign in front — and when the commander's fleet damages
// another hull — the shield white, with no sign. A torpedo figure is
// `torpedo_scale` of the gun size at weight 700 with a filled dart before
// the digits; a splash keeps the gun size and a hollow dart. Nothing else
// on the map draws a figure, because a figure a bystander cannot act on is
// clutter.
//
// **The layer also draws the two pluses** (R725). A Tender's work lands on
// the commander's own hulls, so the mark that says so belongs here: `heal`
// puts a `+` beside the bar that moved, and the plus takes the rise motion
// whatever style the player picked for a hit.
//
// **A figure is a SCREEN thing.** It holds `size_px` at every zoom, so
// every quad carries `F_SCREEN` and every offset below is in CSS px. The
// layer converts a screen offset to the world pivot by dividing it by the
// camera zoom, which is exactly what the shader then undoes — so the mark
// lands where the arithmetic says and the atlas cell is never resampled.
//
// **A figure may WAIT for the round that carries it** (R1010). A gun HIT event
// fires when the server sums the second, and the salvo of that second is still
// in the water; `DamageHit.waitSec` holds the figure back until the first round
// lands. A figure of a negative age packs nothing until it reaches zero, which
// is the rule the Tender's second plus already follows.
//
// **A borrower may ask for a larger figure** (R552). The replay film zooms
// in on a fight, and a 12 px figure beside a hull three times its map size
// read too small. `setScale` multiplies EVERY screen length of the layer —
// the glyphs, the rise, the stack step, the gap to the hull — and leaves
// the world lengths (the hull clearance, the pin's ring) alone. The pack
// does it by handing the five motions a zoom DIVIDED by the scale: every
// screen offset they divide by it comes out `scale` times larger in world
// px, the shader undoes the true zoom, and the quad's own scale pays the
// factor on the cell. The cells are baked at four times their size, so
// the film's factor still leaves two texels to a CSS px.
//
// **The canvas-text carve-out.** §1 says all text is DOM. The figure takes
// the exemption the control-group badge took (R427): the glyphs come from
// the atlas as cells, and the layer draws one quad a glyph. It writes no
// DOM and measures no string — the face is monospaced, so the layout is
// arithmetic on `damageAdvancePx`.
//
// **Five motions**, from mockup round thirty, cards 01, 02, 03, 04 and 10.
// Every constant of every one of them is `DAMAGE_FIGURE` in the generated
// presentation pack; nothing here is a literal a designer would want to
// move. The player picks one in the hub SETTINGS panel or the ☸ OPTIONS
// card, and `rise` is the default.

import { DAMAGE_FIGURE, FLEET_ASPECT, FLEET_TENDER } from '../../gen/presentation';
import { HitAspect } from '../../gen/proto/aquaphobia';
// The aspect marks of the figure (R2407, §6.3.2).
import { chevronCount, higherAspect } from '../../render/aspect-chevrons';
import { factionColor } from '../../world';
import { inkOf } from '../../render/tender';
import { clamp01 } from '../../render/recipe-kit';
import {
  DAMAGE_DART_GAP_EM, DAMAGE_DART_H_EM, DAMAGE_DART_KEY,
  DAMAGE_CHEV_KEY, DAMAGE_DART_HOLLOW_KEY, DAMAGE_DART_LEN_K, DAMAGE_DART_PX,
  DAMAGE_DIAMOND_KEY,
  damageAdvancePx, damageGlyphKey, glyphCellKey, glyphTint,
} from '../gfx/atlas';
import type { DamageStyle } from '../../ui/settings';
import { F_SCREEN, type DrawCtx, type FrameInfo, type Layer } from '../layer';
import type { BatchSource, QuadSink, SegmentSink, SpriteEntry } from './batches';
import { zoomOf } from './shared';

/** What `show` is told about one hit. Lengths are world px. */
export interface DamageHit {
  /** Where the hit landed, as a fallback anchor for an unknown victim. */
  x: number;
  y: number;
  /** The struck fleet's id. 0 when the server named nobody. */
  victim: number;
  /** The figure itself, in points. It is rounded for the digits. */
  amount: number;
  /** True when the commander's own hull lost it: the red and the sign. */
  taken: boolean;
  torpedo: boolean;
  /** A splash on a hull beside the primary target: gun size, hollow dart. */
  splash: boolean;
  /**
   * The struck fleet's faction. A dealt figure wears that faction's ink,
   * so the number and the hull it came off share one colour (the owner's
   * ruling of 2026-09-05, R538). 0 falls back to `dealt_color`.
   */
  faction: number;
  /** The shooter, or the launch point. 0/0 when nothing fired. */
  sourceX: number;
  sourceY: number;
  /**
   * How long the figure waits before it shows, in seconds (R1010).
   *
   * A gun HIT event fires when the shot lands in the sim (R1135), and the
   * rounds of that shot's salvo are still in the water. The figure therefore waits for
   * the FIRST round to land, so the number and the mark arrive together. The
   * gun-shot layer answers the wait and caps it, and a figure of a negative age
   * ages like every other one and packs nothing until it reaches zero — the
   * rule the Tender's second plus already follows.
   */
  waitSec?: number;
  /**
   * True for a live gun shot (R1129, R1136): the figure merges into the
   * live gun figure of the same victim and side when that one is younger
   * than `mergeSec`. The replay film leaves it unset, because a film record
   * is already one coalesced run.
   */
  merge?: boolean;
  /**
   * The arc a primary gun shot struck (R2407, §6.3.2): a beam hit carries
   * one chevron after the digits and a stern hit two. A torpedo, a splash
   * and a bow hit carry none.
   */
  aspect?: HitAspect;
  /** The shot dealt the ambush multiplier: the hollow diamond in front. */
  ambush?: boolean;
  /**
   * The figure is a repair and not damage (R2779, R2783): a Wrasse put
   * `amount` back on the hull. It reads `+N` in the green of the Tender's
   * hull plus, at the gun size with no dart, and it never merges.
   */
  heal?: boolean;
}

/**
 * How many figures may stand at once.
 *
 * A brawl on a busy map is the worst case, and the oldest figure is the
 * one a reader has already read — so the cap drops from the front rather
 * than refusing the newest hit, which is the one that matters.
 */
const MAX_FIGURES = 64;

/**
 * Where a figure sits above the hull mark, in screen px.
 *
 * An own hull carries its two bars ABOVE it, at world y −31..−22
 * (`fleets.ts`), so the figure clears 31 world px of hull and bar plus a
 * gap. A contact carries its bars below, so the figure only clears the
 * hull ring. Both are `k · zoom + gap`, because the hull scales with the
 * map and the gap does not.
 */
const OWN_CLEAR_WORLD = 31;
const CONTACT_CLEAR_WORLD = 12;
const ANCHOR_GAP_PX = 8;

/**
 * Where the two pluses clear their own bar, in world px (R725).
 *
 * A plus states which repair landed, so it rises off the bar that moved and
 * not off the top of the stack. The own hull bar sits at world y −22 and the
 * shield bar at −26 (`fleets.ts`), so each plus clears its own bar alone.
 */
const HULL_BAR_CLEAR_WORLD = 22;
const SHIELD_BAR_CLEAR_WORLD = 26;

/**
 * The atlas key of every glyph of one figure's text. The gather figure of
 * the fleet layer rides the same cells (R1123), so it builds its keys here.
 */
export function glyphKeys(weight: number, text: string): string[] {
  const out: string[] = [];
  for (let i = 0; i < text.length; i++) out.push(damageGlyphKey(weight, text[i]));
  return out;
}

/** The one glyph a plus writes, from the damage alphabet (R724). */
const PLUS_KEYS = glyphKeys(DAMAGE_FIGURE.weight, '+');

/** The hull ring the leader pin measures from, in world px (`fleets.ts`). */
const PIN_HULL_R_WORLD = 22;
/** Where the leader starts, in world px: inside the ring, on the hull. */
const PIN_LEAD_R_WORLD = 11;
/** What stands between the pin's slot and the first glyph, in screen px. */
const PIN_TEXT_GAP_PX = 4;
/** The leader hairline: one screen px, in the figure's own ink. */
const PIN_LEAD_WIDTH_PX = 1;
const PIN_LEAD_ALPHA = 0.8;

const DEG = Math.PI / 180;

/** The card's ease-out, clamped first: an age past its span holds at 1. */
const easeOut = (u: number) => 1 - (1 - clamp01(u)) ** 3;

/** One live figure. Allocated at `show` or `heal`, and never again. */
interface Figure {
  /**
   * What the figure states: a hit, or the plus of a Tender's work (R725).
   *
   * A plus always takes the rise motion, whatever style the player picked: a
   * heal is not a hit, and a heal that flew out along a shot would name a
   * shooter that does not exist. A hull plus takes the `+1` side and a shield
   * plus the `−1` side, which is how `heal` finds the pair it must space.
   */
  kind: 'hit' | 'plus';
  victim: number;
  /** The anchor in world px. It follows the victim while the victim is known. */
  x: number;
  y: number;
  age: number;
  /** The age the figure was born at: 0, or minus its wait for the round. */
  born: number;
  /** The damage the figure states, before rounding; a merge adds to it. */
  amount: number;
  life: number;
  taken: boolean;
  torpedo: boolean;
  splash: boolean;
  /** −1 or +1: which side of the mark this figure took. */
  side: number;
  /** How much of the hull this figure clears, in world px. */
  clearWorld: number;
  /** How far the rise motion lifts it, in screen px. */
  risePx: number;
  /**
   * The atlas key of every glyph, sign included. Built once with the figure:
   * a key is a string, and the pack must build none (§10.4).
   */
  keys: readonly string[];
  color: number;
  /** The unit vector from the source to the anchor, for `fly-out`. */
  dirX: number;
  dirY: number;
  /** The highest arc of the figure's shots, and whether one was an ambush. */
  aspect: HitAspect;
  ambush: boolean;
}

/**
 * Which figure a hit deserves, if any.
 *
 * `taken` when the struck fleet is the commander's, `dealt` when the
 * commander fired, and NOTHING for a fight between two other empires: a
 * figure a reader cannot act on is clutter, and the blast already says
 * that something happened there.
 *
 * The victim's own flag wins over the ids, because a contact the server
 * marked as ours is ours whatever `subject` carries.
 */
export function classifyDamage(
  playerId: number, actor: number, subject: number, victimIsOwn: boolean | null,
): 'taken' | 'dealt' | null {
  if (victimIsOwn === true) return 'taken';
  if (playerId !== 0 && subject === playerId) return 'taken';
  if (playerId !== 0 && actor === playerId) return 'dealt';
  return null;
}

/** Where a fleet is now, or null when the client has lost sight of it. */
export type FleetAt = (fleetId: number) => { x: number; y: number } | null;

export class DamageLayer implements Layer {
  readonly name = 'damage';

  private readonly quads: QuadSink;
  private readonly leaders: SegmentSink;
  private readonly items: Figure[] = [];
  private readonly sprite: (key: string) => SpriteEntry;
  /** The map's own answer, kept so a borrower can be given the layer back. */
  private readonly homeAt: FleetAt;
  private fleetAt: FleetAt;
  private style: DamageStyle = 'rise';
  private shown = true;
  /** Set while `setAnchorSource` holds another feed's answer. */
  private lent = false;
  /** Every screen length of a figure, as a multiple of the map's (R552). */
  private figureScale = 1;
  /**
   * Which side the NEXT figure of a victim takes.
   *
   * Two figures on one hull inside a second must not overprint, so they
   * alternate (`sideOf` of the mockup). The map is per victim, so two
   * hulls under fire do not push each other off their own side.
   */
  private readonly nextSide = new Map<number, number>();

  constructor(src: BatchSource, fleetAt: FleetAt) {
    // One quad a glyph, one batch. Sixty-four figures of five glyphs each
    // is the cap, and the dart adds one more to a torpedo figure.
    this.quads = src.newQuads(512);
    this.leaders = src.newSegments(MAX_FIGURES);
    this.sprite = (key) => src.sprite(key);
    this.homeAt = fleetAt;
    this.fleetAt = fleetAt;
  }

  /**
   * Lends the layer to another feed, which is the replay film (R538).
   *
   * A film plays hulls of its own on the live engine, so a figure it fires
   * must follow the REPLAY pose of the struck hull and not the live fleet
   * of the same id. The film installs its own answer on open and hands the
   * layer back with `null` on close.
   *
   * While the layer is borrowed the live feed draws no figure of its own
   * (`MapScene.showDamage`), because a figure of the fight running now
   * belongs to a map the film has covered.
   */
  setAnchorSource(at: FleetAt | null): void {
    this.fleetAt = at ?? this.homeAt;
    this.lent = at !== null;
  }

  /** True while another feed owns the figures. The live feed reads it. */
  get borrowed(): boolean { return this.lent; }

  // -- what the feed fires ---------------------------------------------------

  /**
   * Puts one figure on the water.
   *
   * A live gun figure (`merge`) MERGES into the live gun figure of the same victim and the
   * same side of the exchange when that one is younger than `mergeSec` of
   * wall time (R1129, R1136). The server raises one event per victim per
   * shot (R1135), so ten shooters on one hull would otherwise print ten
   * numbers. `mergeSec` is under the shortest wall fire period, so the
   * shots of one gun stay apart and only the shots of several guns that
   * land together merge. The merged figure states the sum and keeps its
   * side, its age and its motion. A torpedo figure never merges: a torpedo
   * is one round.
   */
  show(hit: DamageHit): void {
    const d = DAMAGE_FIGURE;
    if (hit.heal) { this.repair(hit); return; }
    if (hit.merge && !hit.torpedo && hit.victim !== 0) {
      const into = this.mergeTarget(hit.victim, hit.taken);
      if (into) {
        into.amount += hit.amount;
        // A merged figure shows the highest arc of its shots, and the
        // diamond when any shot was an ambush (R2407).
        into.aspect = higherAspect(into.aspect, hit.aspect ?? HitAspect.HIT_ASPECT_NONE);
        into.ambush ||= hit.ambush === true;
        into.keys = glyphKeys(d.weight,
          (into.taken ? '-' : '') + String(Math.max(1, Math.round(into.amount))));
        return;
      }
    }
    const amount = Math.max(1, Math.round(hit.amount));
    const side = this.nextSide.get(hit.victim) ?? 1;
    this.nextSide.set(hit.victim, -side);
    // The direction the fly-out takes: away from whoever fired. With no
    // source the figure goes straight up, which reads as "we do not know
    // where that came from" rather than as a bearing that is wrong.
    let dirX = 0;
    let dirY = -1;
    if (hit.sourceX !== 0 || hit.sourceY !== 0) {
      const dx = hit.x - hit.sourceX;
      const dy = hit.y - hit.sourceY;
      const n = Math.hypot(dx, dy);
      if (n > 1e-6) { dirX = dx / n; dirY = dy / n; }
    }
    const born = -Math.max(0, hit.waitSec ?? 0);
    this.add({
      kind: 'hit',
      victim: hit.victim,
      x: hit.x,
      y: hit.y,
      age: born,
      born,
      amount: hit.amount,
      life: this.lifeOf(hit.torpedo && !hit.splash),
      taken: hit.taken,
      torpedo: hit.torpedo,
      splash: hit.splash,
      side,
      clearWorld: hit.taken ? OWN_CLEAR_WORLD : CONTACT_CLEAR_WORLD,
      risePx: d.rise.risePx,
      keys: glyphKeys(hit.torpedo ? d.torpedoWeight : d.weight,
        (hit.taken ? '-' : '') + String(amount)),
      color: hit.taken ? d.takenColor
        : hit.faction > 0 ? factionColor(hit.faction) : d.dealtColor,
      dirX,
      dirY,
      // A torpedo pays no aspect (numbers.md §4.1), so it carries no mark.
      aspect: hit.torpedo ? HitAspect.HIT_ASPECT_NONE : hit.aspect ?? HitAspect.HIT_ASPECT_NONE,
      ambush: !hit.torpedo && hit.ambush === true,
    });
  }

  /**
   * The repair figure of a Wrasse (R2783): `+N` in the green of the
   * Tender's hull plus, so a repair reads as the Tender's work reads. It
   * takes the side and the rise of a hit figure, and no aspect mark.
   */
  private repair(hit: DamageHit): void {
    const d = DAMAGE_FIGURE;
    const side = this.nextSide.get(hit.victim) ?? 1;
    this.nextSide.set(hit.victim, -side);
    const born = -Math.max(0, hit.waitSec ?? 0);
    this.add({
      kind: 'hit', victim: hit.victim, x: hit.x, y: hit.y, age: born, born,
      amount: hit.amount, life: this.lifeOf(false),
      taken: false, torpedo: false, splash: hit.splash, side,
      clearWorld: OWN_CLEAR_WORLD, risePx: d.rise.risePx,
      keys: glyphKeys(d.weight, '+' + String(Math.max(1, Math.round(hit.amount)))),
      color: FLEET_TENDER.plus.hullColor,
      dirX: 0, dirY: -1,
      aspect: HitAspect.HIT_ASPECT_NONE, ambush: false,
    });
  }

  /**
   * Puts one figure on the water. The oldest figure is the one already
   * read, so the cap drops the front and never refuses the newest.
   */
  private add(f: Figure): void {
    this.items.push(f);
    while (this.items.length > MAX_FIGURES) this.items.shift();
  }

  /** The newest live gun figure a new gun figure of `victim` merges into. */
  private mergeTarget(victim: number, taken: boolean): Figure | null {
    for (let i = this.items.length - 1; i >= 0; i--) {
      const f = this.items[i];
      if (f.kind !== 'hit' || f.torpedo || f.victim !== victim || f.taken !== taken) continue;
      return f.age - f.born < DAMAGE_FIGURE.mergeSec ? f : null;
    }
    return null;
  }

  /**
   * Puts one plus on the water: the Tender's work landed (R725, §6.3.2).
   *
   * A **hull** plus rises off the hull bar on the `+1` side, and a **shield**
   * plus rises off the shield bar on the `−1` side. The two are disjoint, so
   * a reader can tell which repair landed without reading a number.
   *
   * The shield plus WAITS `plus.gapSec` when a hull plus of the same hull is
   * still younger than that gap. A figure of a negative age ages like every
   * other one and packs nothing until it reaches zero, so the two marks never
   * rise together off one hull.
   *
   * The layer draws nothing while a film has borrowed it: the film plays a
   * fight that is over, and a Tender at work now belongs to the map under it.
   */
  heal(
    victim: number, x: number, y: number,
    kind: 'hull' | 'shield', faction: number,
  ): void {
    if (this.lent) return;
    const t = FLEET_TENDER.plus;
    const hull = kind === 'hull';
    const verdict = hull ? t.hullColor : t.shieldColor;
    let age = 0;
    if (!hull) {
      for (const f of this.items) {
        if (f.kind !== 'plus' || f.victim !== victim || f.side < 0) continue;
        if (f.age < t.gapSec) age = Math.min(age, f.age - t.gapSec);
      }
    }
    this.add({
      kind: 'plus',
      victim,
      x,
      y,
      age,
      born: age,
      amount: 0,
      life: t.lifeSec,
      taken: false,
      torpedo: false,
      splash: false,
      side: hull ? 1 : -1,
      clearWorld: hull ? HULL_BAR_CLEAR_WORLD : SHIELD_BAR_CLEAR_WORLD,
      risePx: t.risePx,
      keys: PLUS_KEYS,
      color: inkOf(t.ink, faction, verdict, factionColor),
      dirX: 0,
      dirY: -1,
      aspect: HitAspect.HIT_ASPECT_NONE,
      ambush: false,
    });
  }

  /** Which motion the figures take. A change applies to the live ones. */
  setStyle(style: DamageStyle): void { this.style = style; }

  /**
   * How many times its map size a figure is drawn at (R552). The replay
   * film sets it on open and puts 1 back on close; the live map never
   * calls it. A value that is not a positive number is read as 1.
   */
  setScale(k: number): void { this.figureScale = k > 0 && Number.isFinite(k) ? k : 1; }

  /** The multiple `setScale` holds. */
  get scale(): number { return this.figureScale; }

  /** Takes every figure off the water now (a season roll, a film seek). */
  clear(): void {
    this.items.length = 0;
    this.nextSide.clear();
  }

  /** How many figures are alive. The tests and the stats line read it. */
  get count(): number { return this.items.length; }

  // -- the frame -------------------------------------------------------------

  update(frame: FrameInfo): void {
    const dt = frame.dtMs / 1000;
    // The live figures close up in place, in arrival order, so a death
    // allocates nothing (§10.4) and the stack and the pin keep their order.
    const items = this.items;
    let live = 0;
    for (let i = 0; i < items.length; i++) {
      const f = items[i];
      f.age += dt;
      if (f.age > f.life) continue;
      items[live++] = f;
      // The figure follows its hull while the hull is in sight. A contact
      // that went dark keeps the last place it was seen, because a figure
      // that jumped to the origin would read as a hit somewhere else.
      const at = f.victim === 0 ? null : this.fleetAt(f.victim);
      if (at) { f.x = at.x; f.y = at.y; }
    }
    items.length = live;
    this.pack(frame);
  }

  draw(ctx: DrawCtx): void {
    this.leaders.flush(ctx.cam);
    this.quads.flush(ctx.cam);
  }

  setVisible(v: boolean): void { this.shown = v; }
  get visible(): boolean { return this.shown; }

  // -- the five motions ------------------------------------------------------

  /** The life one figure gets, which only `rise` varies by round. */
  private lifeOf(bigTorpedo: boolean): number {
    const d = DAMAGE_FIGURE;
    switch (this.style) {
      case 'pop-and-drop': return d.pop.lifeSec;
      case 'fly-out': return d.fly.lifeSec;
      case 'stack': return d.stack.lifeSec;
      case 'leader-pin': return d.pin.lifeSec;
      default: return bigTorpedo ? d.rise.torpedoLifeSec : d.rise.lifeSec;
    }
  }

  private pack(frame: FrameInfo): void {
    this.quads.begin();
    this.leaders.begin();
    // The motions take the zoom over the figure scale (see the file head):
    // every screen offset they divide by it lands `figureScale` times
    // larger, and `emit` and `put` pay the same factor on the cells.
    const zoom = zoomOf(frame.cam.zoom) / this.figureScale;
    const items = this.items;
    for (let i = 0; i < items.length; i++) {
      const f = items[i];
      // A plus that is still waiting out its gap draws nothing yet (R725).
      if (f.age < 0) continue;
      // A plus always rises, whatever style the player picked.
      if (f.kind === 'plus') { this.packRise(f, zoom); continue; }
      switch (this.style) {
        case 'pop-and-drop': this.packPop(f, zoom); break;
        case 'fly-out': this.packFly(f, zoom); break;
        case 'stack': this.packStack(f, i, zoom); break;
        case 'leader-pin': this.packPin(f, i, zoom); break;
        default: this.packRise(f, zoom); break;
      }
    }
  }

  /** Card 01, RISE AND FADE: it rises on an ease-out and then fades. */
  private packRise(f: Figure, zoom: number): void {
    const d = DAMAGE_FIGURE;
    const u = f.age / f.life;
    const dx = f.side * d.sideOffsetPx;
    const dy = this.anchorPx(f, zoom) - f.risePx * easeOut(u);
    this.emit(f, zoom, dx, dy, this.fade(u, d.rise.hold), this.punch(f), 'center');
  }

  /**
   * Card 02, POP AND DROP: it lands large, settles, hangs still for the
   * read window, then falls under gravity and drifts away from the
   * shooter, so it never crosses the hull it came from.
   */
  private packPop(f: Figure, zoom: number): void {
    const d = DAMAGE_FIGURE;
    const p = d.pop;
    // Away from whoever fired, on the x axis alone: the fall owns the y.
    const away = f.dirX >= 0 ? 1 : -1;
    let dx = away * d.sideOffsetPx;
    let dy = this.anchorPx(f, zoom);
    let alpha = 1;
    if (f.age > p.hangSec) {
      const t = f.age - p.hangSec;
      dy += 0.5 * p.gravityPxPerSec2 * t * t;
      dx += away * p.driftPxPerSec * t;
      alpha = 1 - t / (p.lifeSec - p.hangSec);
    }
    const scale = 1 + (p.appearScale - 1) * (1 - easeOut(f.age / p.settleSec));
    this.emit(f, zoom, dx, dy, alpha, scale, 'center');
  }

  /**
   * Card 03, FLY-OUT ALONG THE SHOT: it travels along the shot vector,
   * through the struck hull and out the far side, slowing on an ease-out.
   * The motion says where the fire came from.
   */
  private packFly(f: Figure, zoom: number): void {
    const d = DAMAGE_FIGURE;
    const u = f.age / f.life;
    const travel = f.torpedo && !f.splash ? d.fly.torpedoTravelPx : d.fly.travelPx;
    const dist = travel * easeOut(u);
    // The figure starts nearer the hull than the other cards, because it
    // leaves at once and a full clearance would put it in empty water.
    const dx = f.dirX * dist;
    const dy = this.anchorPx(f, zoom) * 0.4 + f.dirY * dist;
    this.emit(f, zoom, dx, dy, this.fade(u, d.fly.hold), 1, 'center');
  }

  /**
   * Card 04, STACK: every hull keeps its own column. The newest figure
   * sits just above the bars and each older one is pushed up `step_px`,
   * eased over `push_sec`, so the column scrolls and never jumps.
   *
   * `i` is the figure's own index in `items`.
   */
  private packStack(f: Figure, i: number, zoom: number): void {
    const d = DAMAGE_FIGURE;
    const st = d.stack;
    // The row this figure stands on, newest first, and the age of the
    // newcomer that is pushing the column. Both are counted rather than
    // grouped, so the frame allocates nothing.
    // Two figures out of one events frame share an age (a gun sum and a
    // torpedo on one hull), so arrival order breaks the tie: the items
    // array is in arrival order, and a later index is the newer figure.
    let row = 0;
    let newest = f.age;
    for (let j = 0; j < this.items.length; j++) {
      const o = this.items[j];
      if (o === f || o.victim !== f.victim) continue;
      if (o.age < f.age || (o.age === f.age && j > i)) row++;
      if (o.age < newest) newest = o.age;
    }
    if (row >= st.maxRows) return;
    const k = row === 0 ? 0 : (row - 1) + easeOut(newest / st.pushSec);
    const fade = f.age < st.fadeAtSec
      ? 1 : 1 - (f.age - st.fadeAtSec) / (st.lifeSec - st.fadeAtSec);
    const alpha = fade * Math.min(1, f.age / st.pushSec);
    this.emit(f, zoom, 0, this.anchorPx(f, zoom) - k * st.stepPx, alpha, 1, 'center');
  }

  /**
   * Card 10, LEADER PIN: the figure holds still at one of the ring's
   * slots, joined to the hull by a hairline in its own ink. Several hits
   * inside the hold take the next slot, so a brawl reads as a clock face
   * and never as a pile.
   *
   * `i` is the figure's own index in `items`.
   */
  private packPin(f: Figure, i: number, zoom: number): void {
    const d = DAMAGE_FIGURE;
    const pin = d.pin;
    // The slot, counted by how many older figures this hull already holds.
    // Same tie-break as the stack: an equal age is an earlier index.
    let k = 0;
    for (let j = 0; j < this.items.length; j++) {
      const o = this.items[j];
      if (o === f || o.victim !== f.victim) continue;
      if (o.age > f.age || (o.age === f.age && j < i)) k++;
    }
    const th = pin.slotsDeg[k % pin.slotsDeg.length] * DEG;
    const cos = Math.cos(th);
    const sin = Math.sin(th);
    const r = PIN_HULL_R_WORLD * zoom + pin.ringPx;
    const r0 = PIN_LEAD_R_WORLD * zoom;
    const ramp = f.age < pin.holdSec
      ? Math.min(1, f.age / pin.growSec)
      : 1 - (f.age - pin.holdSec) / (pin.lifeSec - pin.holdSec);
    const alpha = clamp01(ramp);
    // The leader. Its ENDPOINTS are world px (the segment shader takes
    // F_SCREEN for the width alone), so the screen radii divide by zoom.
    const tip = r0 + (r - r0) * alpha;
    this.leaders.line(
      f.x + cos * r0 / zoom, f.y + sin * r0 / zoom,
      f.x + cos * tip / zoom, f.y + sin * tip / zoom,
      PIN_LEAD_WIDTH_PX, f.color, alpha * PIN_LEAD_ALPHA, F_SCREEN,
    );
    const right = cos >= 0;
    this.emit(
      f, zoom,
      cos * r + (right ? PIN_TEXT_GAP_PX : -PIN_TEXT_GAP_PX), sin * r,
      alpha, 1, right ? 'left' : 'right',
    );
  }

  // -- the shared arithmetic -------------------------------------------------

  /** Where the figure clears the hull, in screen px against the mark. */
  private anchorPx(f: Figure, zoom: number): number {
    return -(f.clearWorld * zoom + ANCHOR_GAP_PX);
  }

  /** Full ink for `hold` of the life, then a linear fade to nothing. */
  private fade(u: number, hold: number): number {
    return u < hold ? 1 : 1 - (u - hold) / (1 - hold);
  }

  /** The arrival punch a torpedo figure lands with. A gun figure has none. */
  private punch(f: Figure): number {
    if (!f.torpedo) return 1;
    const d = DAMAGE_FIGURE;
    return 1 + (d.punchScale - 1) * (1 - easeOut(f.age / d.punchSec));
  }

  /**
   * Writes one figure: the dart, then the digits, left to right.
   *
   * `dx`/`dy` are the figure's place in SCREEN px against the hull mark,
   * and `align` says whether that place is the middle, the left edge or
   * the right edge of the whole figure. Every quad carries `F_SCREEN`, so
   * the pivot takes the offset divided by the zoom and the shader undoes
   * the division: the mark holds its size at every zoom.
   */
  private emit(
    f: Figure, zoom: number, dx: number, dy: number,
    alpha: number, scale: number, align: 'center' | 'left' | 'right',
  ): void {
    if (alpha <= 0 || scale <= 0) return;
    const d = DAMAGE_FIGURE;
    const big = f.torpedo && !f.splash;
    // In screen px BEFORE the figure scale: every length laid out from it
    // is divided by the scaled zoom in `put`, which pays the factor (R552).
    const px = d.sizePx * (big ? d.torpedoScale : 1) * scale;
    const weight = f.torpedo ? d.torpedoWeight : d.weight;
    // The cells of one weight are baked at that weight's own size, so a
    // splash — weight 700 at the gun size — is the one figure that scales.
    const glyphScale = px / (weight === d.torpedoWeight
      ? d.sizePx * d.torpedoScale : d.sizePx);
    const adv = damageAdvancePx(px);
    const dartL = f.torpedo ? px * DAMAGE_DART_H_EM * DAMAGE_DART_LEN_K : 0;
    // The aspect marks (R2407): the diamond of an ambush takes the place
    // the dart takes on a torpedo figure, and the chevrons follow the
    // digits. Their cells are baked at the gun size.
    const a = FLEET_ASPECT;
    const markScale = px / d.sizePx;
    const pre = f.torpedo ? dartL + px * DAMAGE_DART_GAP_EM
      : f.ambush ? px * a.figureDiamondAdvEm : 0;
    const chevrons = chevronCount(f.aspect);
    const step = px * a.figureStepEm;
    const suffix = chevrons > 0 ? a.figureGapPx * markScale + chevrons * step : 0;
    const digits = adv * f.keys.length;
    const total = pre + digits + suffix;
    const x0 = align === 'left' ? dx : align === 'right' ? dx - total : dx - total / 2;
    const ink = clamp01(alpha);
    if (f.torpedo) {
      // Both darts are baked at the torpedo size, so the splash's hollow
      // dart scales down with its digits and the two stay in proportion.
      this.put(f, zoom, x0 + dartL / 2, dy, f.color, ink, px / DAMAGE_DART_PX,
        f.splash ? DAMAGE_DART_HOLLOW_KEY : DAMAGE_DART_KEY);
    } else if (f.ambush) {
      this.put(f, zoom, x0 + pre / 2, dy, f.color, ink, markScale, DAMAGE_DIAMOND_KEY);
    }
    for (let i = 0; i < f.keys.length; i++) {
      this.put(f, zoom, x0 + pre + adv * (i + 0.5), dy, f.color, ink, glyphScale, f.keys[i]);
    }
    const c0 = x0 + pre + digits + a.figureGapPx * markScale;
    for (let i = 0; i < chevrons; i++) {
      this.put(f, zoom, c0 + step * (i + 0.5), dy, f.color, ink, markScale, DAMAGE_CHEV_KEY);
    }
  }

  /**
   * One glyph or dart, at a screen offset from the hull's world place.
   *
   * `zoom` is the camera zoom over the figure scale, so the offset lands
   * `figureScale` times larger in world px; the shader undoes the TRUE
   * zoom, and the cell's own scale pays the factor (R552).
   */
  private put(
    f: Figure, zoom: number, dx: number, dy: number,
    color: number, alpha: number, scale: number, key: string,
  ): void {
    // The light identity draws the cell baked in the figure's ink over
    // its paper halo, with no tint (R3296).
    this.quads.sprite(
      f.x + dx / zoom, f.y + dy / zoom, 0,
      this.sprite(glyphCellKey(key, color)), glyphTint(color), alpha, scale * this.figureScale, F_SCREEN,
    );
  }
}
